import React, { FunctionComponent, ReactElement, useCallback, useState } from 'react'
import Grid from '@sport1/react-elements/Grid'
import dayjs from 'dayjs'
import Divider from '../BaseComponents/Divider'
import SmallBodyFont from '../Theme/Fonts/SmallBody'
import BodyFont from '../Theme/Fonts/Body'
import MonthNavigator from './HelperClass/MonthNavigator'
import DateBlock from './HelperClass/DateBlock'
import { DatePickerProps, DateType } from './Types'
import { getCalenderPageDays, getPadding } from './Utils'
import { getDateBlocksDimensions } from './Utils/GetDateBlocksDimensions'

const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

const DatePicker: FunctionComponent<DatePickerProps> = ({
    onPressDate,
    selectedDate = dayjs(),
    selectableDaysRange,
    testID,
}): ReactElement => {
    const { dateBlocksDimension, dateBlockBorderRadius, isMaxSizeApp } = getDateBlocksDimensions()
    const [userSelectedDate, setUserSelectedDate] = useState(selectedDate)
    const [navigatorDate, setNavigatorDate] = useState(selectedDate)
    const [daysToShow, setDaysToShow] = useState(
        getCalenderPageDays({ date: selectedDate, selectableDaysRange })
    )
    const onPressRight = useCallback(() => {
        const date = navigatorDate.add(1, 'month')
        setDaysToShow(getCalenderPageDays({ date, selectableDaysRange }))
        setNavigatorDate(date)
    }, [navigatorDate, selectableDaysRange])

    const onPressLeft = useCallback(() => {
        const date = navigatorDate.subtract(1, 'month')
        setDaysToShow(getCalenderPageDays({ date, selectableDaysRange }))
        setNavigatorDate(date)
    }, [navigatorDate, selectableDaysRange])

    const onPressDateBlock = (day: DateType) => {
        setUserSelectedDate(day.date)
        onPressDate(day)

        if (day.key !== 'MONTH_DAY_RANGE') {
            const params = {
                date: day.date,
                selectableDaysRange,
            }
            setDaysToShow(getCalenderPageDays(params))
            setNavigatorDate(day.date)
        }
    }

    React.useEffect(() => {
        if (userSelectedDate !== selectedDate) {
            setUserSelectedDate(selectedDate)
            setDaysToShow(getCalenderPageDays({ date: selectedDate, selectableDaysRange }))
            setNavigatorDate(selectedDate)
        }
    }, [selectableDaysRange, selectedDate, userSelectedDate])
    return (
        <Grid
            backgroundColor="pearl"
            borderRadius={4}
            testID={testID}
            padding={getPadding(dateBlocksDimension)}
        >
            <MonthNavigator
                isMaxSizeApp={isMaxSizeApp}
                navigatorDate={navigatorDate}
                selectableDaysRange={selectableDaysRange}
                onPressLeft={onPressLeft}
                onPressRight={onPressRight}
            />
            <Divider />
            <Grid horizontal>
                {weekDays.map(label => (
                    <Grid
                        center
                        height={30}
                        width={dateBlocksDimension}
                        marginRight={1}
                        key={`weekday-label-${label}`}
                    >
                        {isMaxSizeApp ? (
                            <BodyFont bold color="onyx">
                                {label}
                            </BodyFont>
                        ) : (
                            <SmallBodyFont bold color="onyx">
                                {label}
                            </SmallBodyFont>
                        )}
                    </Grid>
                ))}
            </Grid>

            <Grid flexWrap="wrap" horizontal>
                {daysToShow.map((day, index) => (
                    <DateBlock
                        day={day}
                        key={`calendar-row-${index}`}
                        dateBlocksDimension={dateBlocksDimension}
                        dateBlockBorderRadius={dateBlockBorderRadius}
                        onPressDateBlock={onPressDateBlock}
                        isMaxSizeApp={isMaxSizeApp}
                        selected={userSelectedDate.isSame(day.date)}
                    />
                ))}
            </Grid>
        </Grid>
    )
}

export default DatePicker
